/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import React, { forwardRef, useState, useEffect } from 'react';
// @mui
import { Box, Stack, Dialog, Button, AppBar, Typography, Slide, Tabs, Tab } from '@mui/material';
import moment from 'moment';
// components
import OverviewTab from 'src/sections/@dashboard/lead/OverviewTab';
import AttendedChecklistV2, { useAttendedChecklistVisibility } from 'src/sections/@dashboard/leadDetail/AttendedChecklistV2';
import NotesTab from 'src/sections/@dashboard/lead/NotesTab';
import IosEmailComposerBody from 'src/sections/tasks/composers/IosEmailComposerBody';
import FollowUpWhatsappChat from 'src/components/whatsappComponents/FollowUpWhatsappChat';
import { callApi } from 'src/utils/apiCall';
import { AttendedAppointment } from '../Attended';
import Iconify from '../Iconify';
import { ToDoList } from '../../sections/@dashboard/lead';
import { clearLead, getLeadNotes, getLeadReminderData } from '../../redux/slices/lead';
import { setCurrentTreatmentDataGlobal } from '../../redux/slices/multiTreatment';
import { useSelector, dispatch } from '../../redux/store';
import {
    selectLeadFlowReminder,
    selectTaskFlowCounter,
    selectLeadNotes,
    selectLeadNotesArr,
} from '../../redux/selectors/leadSelectors';
import {
    commonDialogHeaderStyle,
    onBoardDialogHeader,
    reminderTitleStyle,
    cancelBoxStyle,
    reminderDialogStyle,
} from '../../styles/ScheduleReminder';
import {
    rightBoxInnerStyle,
    leftBoxInnerStyle,
    tabsMainBoxStyle,
    tabButtonStyle,
    tabContentBoxStyle,
    listCounterBoxStyle,
    popupMainStackStyle,
} from '../../styles/ContactLeadPopup';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function OpenAttendedPopup({ isOverview, open, onClose, type = 'lead', user, inputRef, headerButtons, editDetailHandler, OpenAssignTask, handleCloseAndReload, handleReloadLeads, handleNoAttended }) {
    const [sidebarTab, setSidebarTab] = useState('overview');
    const [treatmentData, setTreatmentData] = useState(null);
    const [notesLength, setNotesLength] = useState([]);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const leadFlowReminder = useSelector(selectLeadFlowReminder);
    const taskFlowCounter = useSelector(selectTaskFlowCounter);
    const leadNotes = useSelector(selectLeadNotes);
    const leadNotesArr = useSelector(selectLeadNotesArr);
    const followLead = useSelector((state) => state.lead.followLead);

    const currentLeadId = user?.lead_id || user?.id;
    const leadDetailForChecklist = (followLead && Number(followLead.id) === Number(currentLeadId))
        ? followLead
        : { id: currentLeadId, lead_treatments: user?.lead_treatments || [] };

    const attendedVisibility = useAttendedChecklistVisibility({
        practiceId,
        leadId: currentLeadId,
        leadDetail: leadDetailForChecklist,
    });

    useEffect(() => {
        const currentLeadId = user?.lead_id || user?.id;
        if (!practiceId || !currentLeadId) return;
        setNotesLength([]);
        dispatch(getLeadNotes(currentLeadId, practiceId));
        dispatch(getLeadReminderData(practiceId, 'LeadDetail', 10, 0, 'leadNotes', currentLeadId, '', '', '', 'counter', '', false, true));
    }, [user?.id, user?.lead_id, practiceId]);

    useEffect(() => {
        const fetchData = async () => {
            const currentLeadId = user?.lead_id || user?.id;
            if (!practiceId || !currentLeadId) return;
            try {
                const resp = await callApi(`/api/v1/practices/${practiceId}/leads/${currentLeadId}/treatments`, 'GET');
                setTreatmentData(resp?.data?.results ?? []);
            } catch (error) {
                console.error('Error fetching treatment data:', error);
            }
        };
        fetchData();
    }, [practiceId, user?.id, user?.lead_id]);

    useEffect(() => {
        const combineNotes = [];
        if (leadNotes?.length > 0) {
            combineNotes.push(...leadNotes.filter((el) => el.additional_notes !== ''));
        }
        if (leadNotesArr?.results?.length > 0) {
            combineNotes.push(...leadNotesArr.results.filter((el) => el.note !== ''));
        }
        if (combineNotes.length) {
            setNotesLength(
                combineNotes.sort(
                    (a, b) =>
                        new Date(moment(a.updated_at, 'DD-MM-YYYY HH:mm:ss')) -
                        new Date(moment(b.updated_at, 'DD-MM-YYYY HH:mm:ss'))
                )
            );
        }
    }, [user, leadNotes, leadNotesArr]);

    const handleTabChange = (event, newValue) => setSidebarTab(newValue);

    const handleClose = async (action) => {
        if (action === 'tco') {
            return;
        }
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        onClose();
    };

    const tabCounter = (value) => {
        if (value === 'to-do') {
            return taskFlowCounter?.[0]?.count || 0;
        }
        return 0;
    };

    const labelCount = (data) => {
        if (data.label === 'To-do') {
            return (
                <Box>
                    {data.label}
                    <Box sx={listCounterBoxStyle}>
                        {tabCounter(data.value)}
                    </Box>
                </Box>
            );
        }
        return data.label;
    };

    const SIDEBAR_TABS = [
        {
            icon: 'grommet-icons:overview',
            label: 'Overview',
            value: 'overview',
            component: (
                <OverviewTab
                    treatmentTabData={treatmentData || []}
                    leadId={user?.lead_id || user?.id}
                    setExpandLayout={() => {}}
                    handleChange={handleTabChange}
                    expandLayout={false}
                    leadReminder={leadFlowReminder}
                    user={user}
                    LeadNotes={notesLength}
                    showDetailModes
                />
            ),
            width: 24,
            height: 24,
        },
        ...(attendedVisibility.visible ? [{
            icon: 'mdi:checkbox-marked-outline',
            label: 'Attended Checklist',
            value: 'checklist',
            component: (
                <AttendedChecklistV2
                    practiceId={practiceId}
                    leadId={currentLeadId}
                    leadDetail={leadDetailForChecklist}
                />
            ),
            width: 20,
            height: 20,
        }] : []),
        {
            icon: 'bi:check-circle-fill',
            label: 'To-do',
            value: 'to-do',
            component: (
                <ToDoList
                    setExpandLayout={() => {}}
                    handleChange={handleTabChange}
                    expandLayout={false}
                    leadReminder={leadFlowReminder}
                    user={user}
                    isForTab
                />
            ),
            width: 18,
            height: 18,
        },
        {
            icon: 'solar:chat-round-dots-bold',
            label: 'Notes',
            value: 'notes',
            component: (
                <NotesTab
                    LeadNotes={notesLength}
                    isForTab
                    userDetails={user}
                    handleChange={handleTabChange}
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'mdi:email-outline',
            label: 'Email',
            value: 'email',
            component: (
                <IosEmailComposerBody
                    leadId={followLead?.id || user?.lead_id || user?.id}
                    practiceId={practiceId}
                    leadDetail={followLead || user}
                    compact
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'ic:baseline-whatsapp',
            label: 'WhatsApp',
            value: 'whatsapp',
            component: (
                <FollowUpWhatsappChat
                    user={followLead || user}
                    followLead={followLead}
                />
            ),
            width: 20,
            height: 20,
        },
    ];

    return (
        <Dialog
            fullScreen
            TransitionComponent={Transition}
            open={open}
            onClose={handleClose}
            sx={[reminderDialogStyle(type), { pt: { xs: 0, sm: 0 }, '.MuiDialog-paperFullScreen': { bgcolor: '#F5F5F7', scrollbarGutter: 'stable' } }]}
            disableEnforceFocus
        >
            <AppBar sx={[headerButtons ? commonDialogHeaderStyle : onBoardDialogHeader, { position: 'sticky !important', top: 0, alignItems: 'center' }]}>
                <Typography
                    sx={reminderTitleStyle}
                    variant="h6"
                    component="div"
                >
                    Attended appointment for <span style={{ textTransform: 'capitalize' }}>{user.first_name}</span>
                </Typography>
                {(() => {
                    const followLeadFilterAttend = followLead?.lead_appointment?.filter((el) => el.status === 'BOOKED' || el.status === 'ATTENDED') || [];
                    const apptDt = followLeadFilterAttend[0]?.datetime;
                    if (!apptDt) return null;
                    const parsed = moment(apptDt, ['DD-MM-YYYY HH:mm:ss.SSSSSSZ', moment.ISO_8601], true);
                    if (!parsed.isValid()) return null;
                    const days = moment().startOf('day').diff(parsed.startOf('day'), 'days');
                    const contactedCount = followLead?.lead_contacted_counts?.find(c => c.status === 'CONSULTATION_BOOKED')?.count || 0;
                    const followUpStep = contactedCount + 1;
                    let daysLabel;
                    if (days === 0) {
                        daysLabel = 'Today';
                    } else if (days === 1) {
                        daysLabel = '1 day ago';
                    } else if (days < 0) {
                        daysLabel = `In ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`;
                    } else {
                        daysLabel = `${days} days ago`;
                    }
                    return (
                        <Box sx={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            gap: 1.5,
                            '@keyframes attendedPulse': {
                                '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255,255,255,0.4)' },
                                '50%': { transform: 'scale(1.25)', boxShadow: '0 0 0 4px transparent' },
                                '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 transparent' },
                            },
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    animation: 'attendedPulse 2s ease-in-out infinite',
                                    flexShrink: 0,
                                }}>
                                    <Typography sx={{
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        color: '#007867',
                                        lineHeight: 1,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}>
                                        {followUpStep}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>
                                    Follow up {followUpStep}
                                </Typography>
                            </Box>
                            <Box sx={{ width: '1px', height: '16px', bgcolor: 'rgba(255,255,255,0.30)' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    animation: 'attendedPulse 2s ease-in-out infinite',
                                    flexShrink: 0,
                                }} />
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                    {daysLabel}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })()}
                {headerButtons &&
                    <Box sx={cancelBoxStyle}>
                        <Button
                            color="inherit"
                            variant='outlined'
                            style={{ borderColor: '#FFFFFF', color: '#fff' }}
                            onClick={() => { handleClose('close'); }}
                        >
                            Cancel
                        </Button>
                    </Box>
                }
            </AppBar>
            <Stack spacing={0} sx={[popupMainStackStyle(sidebarTab), { width: sidebarTab === '' ? 'calc(100% - 85px)' : 'calc(100% - 506px)', pb: '88px' }]}>
                {/* Left side - main content */}
                <Box sx={[leftBoxInnerStyle(false, true), { background: '#F5F5F7' }]}>
                    <AttendedAppointment
                        isOverview={isOverview}
                        isFinishButtonAvailable
                        handleCloseAndReload={handleCloseAndReload}
                        handleClose={handleClose}
                        id={user.lead_id}
                        user={user}
                        type='Starting treatment'
                        inputRef={inputRef}
                        OpenAssignTask={OpenAssignTask}
                        editDetailHandler={editDetailHandler}
                        handleReloadLeads={handleReloadLeads}
                        handleNoAttended={handleNoAttended}
                        onOpenSidebarTab={(v) => setSidebarTab(v)}
                    />
                </Box>
                {/* Right side - sidebar with tabs */}
                <Box sx={[rightBoxInnerStyle(false, sidebarTab), { top: '88px', height: 'calc(100vh - 152px)' }]}>
                    {SIDEBAR_TABS.map((tab) => {
                        const isMatched = tab.value === sidebarTab;
                        return isMatched && (
                            <Box key={tab.value} sx={tabContentBoxStyle}>
                                {tab.component}
                            </Box>
                        );
                    })}
                    <Box sx={tabsMainBoxStyle}>
                        <Tabs
                            orientation='vertical'
                            variant='fullWidth'
                            value={sidebarTab}
                            onChange={handleTabChange}
                            sx={{ '.MuiTabs-flexContainer': { gap: 1 } }}
                        >
                            {SIDEBAR_TABS.map((tab, index) => (
                                <Tab
                                    icon={
                                        <Iconify
                                            icon={tab.icon}
                                            width={tab.width}
                                            height={tab.height}
                                            sx={{ mr: '0 !important', mb: '4px' }}
                                        />
                                    }
                                    key={index}
                                    label={labelCount(tab)}
                                    value={tab.value}
                                    sx={tabButtonStyle}
                                />
                            ))}
                        </Tabs>
                    </Box>
                </Box>
            </Stack>
        </Dialog>
    );
}

OpenAttendedPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    type: PropTypes.string,
    user: PropTypes.object,
    inputRef: PropTypes.any,
    headerButtons: PropTypes.bool,
    editDetailHandler: PropTypes.any,
    OpenAssignTask: PropTypes.any,
    handleCloseAndReload: PropTypes.func,
    handleReloadLeads: PropTypes.func,
    isOverview: PropTypes.bool,
    handleNoAttended: PropTypes.func,
};
